// src/lib/opportunity/formatDepositEval.ts
//
// Deposit math for format rules (7b). Deposit base excludes calc-line
// amounts where include_in_deposit is false.

import { round2, safeNumber, safePct } from "./calc.js";
import type { EvaluatedFormatCalcLine } from "./formatCalcEval.js";
import {
  DEFAULT_FORMAT_DEPOSIT_RULE,
  type FormatDepositMode,
  type FormatDepositRuleDef,
} from "./formatDepositTypes.js";

export type DepositComputation = {
  depositBase: number;
  excludedFromDeposit: number;
  depositAmount: number;
  balanceAmount: number;
  saleTotal: number;
};

export function sumExcludedFromDeposit(lines: EvaluatedFormatCalcLine[]): number {
  return round2(
    lines.filter((l) => l.include_in_deposit === false).reduce((s, l) => s + l.amount, 0),
  );
}

export function computeDepositBase(args: {
  saleTotal: number;
  evaluatedLines: EvaluatedFormatCalcLine[];
}): { depositBase: number; excludedFromDeposit: number } {
  const saleTotal = round2(safeNumber(args.saleTotal));
  const excludedFromDeposit = sumExcludedFromDeposit(args.evaluatedLines);
  const depositBase = round2(Math.max(0, saleTotal - excludedFromDeposit));
  return { depositBase, excludedFromDeposit };
}

export function computeDepositFromRule(args: {
  rule?: FormatDepositRuleDef | null;
  depositBase: number;
  saleTotal: number;
}): Pick<DepositComputation, "depositAmount" | "balanceAmount"> {
  const rule = args.rule ?? DEFAULT_FORMAT_DEPOSIT_RULE;
  const saleTotal = round2(safeNumber(args.saleTotal));
  const depositBase = round2(safeNumber(args.depositBase));

  let depositAmount: number;
  if (rule.deposit_mode === "amount") {
    depositAmount = round2(safeNumber(rule.deposit_value));
  } else {
    depositAmount = round2(depositBase * (safePct(rule.deposit_value) / 100));
  }

  if (depositAmount > saleTotal) depositAmount = saleTotal;
  const balanceAmount = round2(Math.max(0, saleTotal - depositAmount));

  return { depositAmount, balanceAmount };
}

export function computeFullDeposit(args: {
  rule?: FormatDepositRuleDef | null;
  saleTotal: number;
  evaluatedLines: EvaluatedFormatCalcLine[];
}): DepositComputation {
  const saleTotal = round2(safeNumber(args.saleTotal));
  const { depositBase, excludedFromDeposit } = computeDepositBase({
    saleTotal,
    evaluatedLines: args.evaluatedLines,
  });
  const { depositAmount, balanceAmount } = computeDepositFromRule({
    rule: args.rule,
    depositBase,
    saleTotal,
  });
  return {
    saleTotal,
    depositBase,
    excludedFromDeposit,
    depositAmount,
    balanceAmount,
  };
}

export function isFormatDepositMode(v: unknown): v is FormatDepositMode {
  return v === "percent" || v === "amount";
}
